import type { PadelSetScore } from '@/services/padelService';

export interface SetScoreInput {
  setNumber: number;
  scoreSideA: number;
  scoreSideB: number;
}

export const PADEL_MAX_SCORE = 32;
export const PADEL_QUICK_PICK_MAX = 15;
export const PADEL_MAX_SETS = 3;
export const PADEL_SET_PICKER_MAX = 7;

export function americanoOpponentScore(selfScore: number, target: number): number {
  return target - selfScore;
}

export function isAmericanoOpponentValid(selfScore: number, target: number): boolean {
  const opponent = americanoOpponentScore(selfScore, target);
  return opponent >= 0 && opponent <= PADEL_MAX_SCORE;
}

export function isQuickPickScoreDisabled(
  pick: number,
  target: number | null | undefined,
  americanoMode: boolean
): boolean {
  if (!americanoMode || target == null) return false;
  return !isAmericanoOpponentValid(pick, target);
}

export function resolveAmericanoScores(
  side: 'a' | 'b',
  newScore: number,
  scoreA: number,
  scoreB: number,
  target: number
): { scoreA: number; scoreB: number } {
  if (side === 'a') {
    const opponent = americanoOpponentScore(newScore, target);
    return isAmericanoOpponentValid(newScore, target)
      ? { scoreA: newScore, scoreB: opponent }
      : { scoreA: newScore, scoreB: scoreB };
  }
  const opponent = americanoOpponentScore(newScore, target);
  return isAmericanoOpponentValid(newScore, target)
    ? { scoreA: opponent, scoreB: newScore }
    : { scoreA: scoreA, scoreB: newScore };
}

export function buildSetsFromGame(
  setScores: PadelSetScore[],
  maxSets: number = PADEL_MAX_SETS
): SetScoreInput[] {
  const out: SetScoreInput[] = [];
  for (let n = 1; n <= maxSets; n++) {
    const row = setScores.find((s) => s.setNumber === n);
    out.push({
      setNumber: n,
      scoreSideA: row?.scoreSideA ?? 0,
      scoreSideB: row?.scoreSideB ?? 0,
    });
  }
  return out;
}

export function setWinner(scoreA: number, scoreB: number): 'a' | 'b' | '' {
  if (scoreA === scoreB) return '';
  return scoreA > scoreB ? 'a' : 'b';
}

export function isSetComplete(scoreA: number, scoreB: number): boolean {
  if (scoreA === scoreB) return false;
  return scoreA > 0 || scoreB > 0;
}

export function countSetWins(sets: SetScoreInput[]): { winsA: number; winsB: number } {
  let winsA = 0;
  let winsB = 0;
  for (const s of sets) {
    if (!isSetComplete(s.scoreSideA, s.scoreSideB)) continue;
    const w = setWinner(s.scoreSideA, s.scoreSideB);
    if (w === 'a') winsA++;
    if (w === 'b') winsB++;
  }
  return { winsA, winsB };
}

export function setsToWin(maxSets: number): number {
  if (maxSets <= 1) return 1;
  return Math.ceil(maxSets / 2);
}

export function canFillSet(setNumber: number, sets: SetScoreInput[], maxSets: number): boolean {
  if (setNumber < 1 || setNumber > maxSets) return false;
  const needed = setsToWin(maxSets);
  for (let n = 1; n < setNumber; n++) {
    const prev = sets.find((s) => s.setNumber === n);
    if (!prev || !isSetComplete(prev.scoreSideA, prev.scoreSideB)) {
      return false;
    }
  }
  const previous = sets.filter((s) => s.setNumber < setNumber);
  const { winsA, winsB } = countSetWins(previous);
  return winsA < needed && winsB < needed;
}

export function isSet3Allowed(sets: SetScoreInput[], maxSets: number = PADEL_MAX_SETS): boolean {
  return canFillSet(3, sets, maxSets);
}

export function pointPickerValues(target: number): number[] {
  const start = Math.max(0, target - 11);
  const values: number[] = [];
  for (let n = start; n <= target; n++) values.push(n);
  return values;
}

export function clampScoreValue(value: number, cap: number = PADEL_MAX_SCORE): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(cap, Math.max(0, Math.trunc(value)));
}

export function applySideDelta(
  scoreSideA: number,
  scoreSideB: number,
  side: 'a' | 'b',
  delta: number,
  cap: number = PADEL_MAX_SCORE
): { scoreSideA: number; scoreSideB: number } {
  return applyScoreValue(
    scoreSideA,
    scoreSideB,
    side,
    (side === 'a' ? scoreSideA : scoreSideB) + delta,
    cap
  );
}

export function applyScoreValue(
  scoreSideA: number,
  scoreSideB: number,
  side: 'a' | 'b',
  value: number,
  cap: number = PADEL_MAX_SCORE
): { scoreSideA: number; scoreSideB: number } {
  const clamped = clampScoreValue(value, cap);
  return {
    scoreSideA: side === 'a' ? clamped : scoreSideA,
    scoreSideB: side === 'b' ? clamped : scoreSideB,
  };
}

export function americanoScoreExample(target: number): string {
  const high = Math.floor((target + 1) / 2);
  const low = target - high;
  return `${high}–${low}`;
}

export function americanoTargetHint(target: number): string {
  return `Target ${target} = total rally kedua tim. Contoh skor valid: ${americanoScoreExample(target)}.`;
}

export function validateAmericanoMatchScore(
  scoreA: number,
  scoreB: number,
  target: number | null | undefined
): string | null {
  if (scoreA < 0 || scoreA > PADEL_MAX_SCORE || scoreB < 0 || scoreB > PADEL_MAX_SCORE) {
    return 'Skor harus antara 0 dan 32';
  }
  if (target == null) {
    return null;
  }
  if (scoreA + scoreB !== target) {
    const example = americanoScoreExample(target);
    return `Total skor harus ${target} (contoh: ${example} untuk target ${target})`;
  }
  return null;
}
